import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { RestorePermissionsDto } from "@swagger/typescript-fetch-goflyer";
import { messages } from "../../../../locales/messages";

interface Props {
  open: boolean;
  onClose: () => void;
  onReopen: () => Promise<unknown>;
  onRestorePermissions: (
    body: RestorePermissionsDto,
  ) => Promise<boolean | undefined>;
}

type Step = "confirm-reopen" | "restore-prompt";

export function ReopenDialog({
  open,
  onClose,
  onReopen,
  onRestorePermissions,
}: Props) {
  const { t } = useTranslation();
  const [step, setStep] = useState<Step>("confirm-reopen");
  const [busy, setBusy] = useState(false);

  const handleClose = () => {
    if (busy) return;
    setStep("confirm-reopen");
    onClose();
  };

  const handleReopen = async () => {
    setBusy(true);
    await onReopen();
    setBusy(false);
    setStep("restore-prompt");
  };

  const handleRestore = async () => {
    setBusy(true);
    await onRestorePermissions({ includeAnyone: true });
    setBusy(false);
    handleClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} data-testid="reopen-dialog">
      {step === "confirm-reopen" && (
        <>
          <DialogTitle>{t(messages.Reopen())}</DialogTitle>
          <DialogContent>
            <DialogContentText>
              {t(messages.Reopen())}?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose} disabled={busy}>
              {t(messages.Cancel())}
            </Button>
            <Button
              variant="contained"
              color="warning"
              onClick={handleReopen}
              disabled={busy}
              data-testid="reopen-confirm"
            >
              {t(messages.Reopen())}
            </Button>
          </DialogActions>
        </>
      )}
      {step === "restore-prompt" && (
        <>
          <DialogTitle>{t(messages.Restore_Permissions())}</DialogTitle>
          <DialogContent>
            <DialogContentText>
              {t(messages.Restore_EDIT_Permissions_Prompt())}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose} disabled={busy}>
              {t(messages.No())}
            </Button>
            <Button
              variant="contained"
              onClick={handleRestore}
              disabled={busy}
              sx={{
                backgroundColor: "#f58220",
                "&:hover": { backgroundColor: "#e8722e" },
              }}
              data-testid="restore-confirm"
            >
              {t(messages.Yes())}
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
}
